import { useClient } from '@cortex/react-query';
import type { OrgView } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import { PageHeader } from '../../../components/common/PageHeader';
import { Table, type IColumn } from '../../../components/common/Table';
import { routes } from '../../../lib/constants';
import { listAllOrgs } from '../../../lib/orgs';
import { guardSuperuser } from '../index';

export const Route = createFileRoute('/internal/organizations/')({
  beforeLoad: () => guardSuperuser(),
  component: OrgsListPage,
});

function OrgsListPage() {
  const client = useClient();
  const nav = useNavigate();

  const orgs = useQuery({
    queryKey: ['admin', 'orgs'],
    queryFn: () => listAllOrgs(client),
  });

  const cols: IColumn<OrgView>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (o) => (
        <Link
          to={routes.internalOrgDetail(o.id)}
          style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}
        >
          {o.name}
        </Link>
      ),
    },
    {
      key: 'alias',
      header: 'Alias',
      mono: true,
      cell: (o) => o.alias ?? <span className="t-muted">—</span>,
    },
    {
      key: 'domains',
      header: 'Domains',
      cell: (o) =>
        o.domains.length === 0 ? (
          <span className="t-muted">—</span>
        ) : (
          o.domains.map((d) => (
            <span key={d.name} className="badge badge--neutral" style={{ marginRight: 4 }}>
              {d.name}
            </span>
          ))
        ),
    },
    {
      key: 'enabled',
      header: 'Status',
      cell: (o) =>
        o.enabled ? (
          <span className="badge badge--success">Active</span>
        ) : (
          <span className="badge badge--neutral">Disabled</span>
        ),
      width: 120,
    },
  ];

  return (
    <>
      <PageHeader
        title="Organizations"
        actions={
          <Link to={routes.INTERNAL_ORGS_NEW} className="btn btn--primary btn--sm">
            New org
          </Link>
        }
      />
      <Table<OrgView>
        rows={orgs.data}
        columns={cols}
        rowKey={(o) => o.id}
        isLoading={orgs.isLoading}
        error={orgs.error as Error | null}
        emptyTitle="No organizations"
        emptyDescription="Create the first one to onboard a customer or internal team."
        onRowClick={async (o) => {
          try {
            await nav({ to: routes.internalOrgDetail(o.id) });
          } catch (err) {
            console.error(err);
          }
        }}
      />
      <p className="t-xs t-muted" style={{ marginTop: 'var(--sp-3)' }}>
        Note: created/updated timestamps aren&apos;t surfaced by Keycloak&apos;s Organizations API.
      </p>
    </>
  );
}
